// src/pages/BankOfferDetail.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, getDocs, collection } from "firebase/firestore";
import { db } from "../firebase";
import cache from "../utils/cache";

function ShareButtons({ url, title, offer }) {
  const [showOptions, setShowOptions] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!offer) return null;

  const isMobile = /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(navigator.userAgent);

  // For bank offers, you may not have offerPrice; if you do, handle similarly.
  const priceText =
    offer.offerPrice === 0 ? "Free 🎉" : offer.offerPrice ? `$${offer.offerPrice} 🙂` : "";

  const buildWhatsAppText = () => {
    const displayVendor = offer.vendor && offer.vendor.includes("##") ? offer.category : offer.vendor;

    return (
      `Hey! 🎉 Check out this bank offer:\n` +
      `*${displayVendor}*\n` +
      `${offer.offer}\n` +
      (priceText ? `*Offer Price:* ${priceText}\n` : "") +
      `*Link:* ${url}`
    );
  };

  const buildPlainText = () => {
    const displayVendor = offer.vendor && offer.vendor.includes("##") ? offer.category : offer.vendor;
    return (
      `Hey! Check out this bank offer:\n` +
      `*${displayVendor}*\n` +
      `${offer.offer}\n` +
      (priceText ? `Offer Price: ${priceText}\n` : "") +
      `Link: ${url}`
    );
  };

  const plainText = buildPlainText();
  const whatsappText = buildWhatsAppText();

  const encodedWhatsAppText = encodeURIComponent(whatsappText);
  const encodedPlainText = encodeURIComponent(plainText);
  const encodedTitle = encodeURIComponent(title || "Bank Offer");

  const whatsappUrl = isMobile
    ? `https://wa.me/?text=${encodedWhatsAppText}`
    : `https://web.whatsapp.com/send?text=${encodedWhatsAppText}`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mt-4 relative">
      <button
        type="button"
        aria-label="Toggle share options"
        onClick={() => setShowOptions((prev) => !prev)}
        className="bg-gray-300 px-4 py-2 rounded hover:bg-gray-400"
      >
        🔗 Share
      </button>

      {showOptions && (
        <div className="flex space-x-3 mt-2 flex-wrap">
          {/* Copy Link */}
          <div className="relative">
            <button
              onClick={copyToClipboard}
              className="bg-gray-200 px-3 py-1 rounded hover:bg-gray-300"
              type="button"
              aria-label="Copy link"
            >
              Copy Link
            </button>
            {copied && (
              <span className="absolute top-full left-0 mt-1 text-xs text-green-600 animate-fade">
                Link copied!
              </span>
            )}
          </div>

          {/* WhatsApp */}
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600"
            aria-label="Share on WhatsApp"
          >
            WhatsApp
          </a>

          {/* SMS */}
          <a
            href={`sms:?&body=${encodedPlainText}`}
            className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
            aria-label="Send SMS"
          >
            SMS
          </a>

          {/* Email */}
          <a
            href={`mailto:?subject=${encodedTitle}&body=${encodedPlainText}`}
            className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
            aria-label="Send Email"
          >
            Email
          </a>

          {/* Facebook */}
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-blue-700 text-white px-3 py-1 rounded hover:bg-blue-800"
            aria-label="Share on Facebook"
          >
            Facebook
          </a>

          {/* X (Twitter) */}
          <a
            href={`https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodeURIComponent(url)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-black text-white px-3 py-1 rounded hover:bg-gray-800"
            aria-label="Share on X"
          >
            X
          </a>

          {/* Telegram */}
          <a
            href={`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodedPlainText}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-cyan-600 text-white px-3 py-1 rounded hover:bg-cyan-700"
            aria-label="Share on Telegram"
          >
            Telegram
          </a>

          {/* Instagram - open IG app (no direct share) */}
          <a
            href="https://www.instagram.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-pink-500 text-white px-3 py-1 rounded hover:bg-pink-600"
            aria-label="Visit Instagram"
          >
            Instagram
          </a>
        </div>
      )}

      <style>{`
        @keyframes fadeOut {
          0% { opacity: 1; }
          90% { opacity: 1; }
          100% { opacity: 0; }
        }
        .animate-fade {
          animation: fadeOut 2s forwards;
        }
      `}</style>
    </div>
  );
}

export default function BankOfferDetail() {
  const { bankName, offerId } = useParams();
  const navigate = useNavigate();

  const [offer, setOffer] = useState(null);
  const [banksLogos, setBanksLogos] = useState({});
  const [banksAddresses, setBanksAddresses] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const decodedBankName = bankName ? decodeURIComponent(bankName) : "";

  useEffect(() => {
    if (!decodedBankName || !offerId) {
      setError("Bank name or offer ID missing.");
      setLoading(false);
      return;
    }

    async function fetchData() {
      setLoading(true);
      setError(null);

      try {
        // Try cache first
        let offersArray;
        if (cache.bankOffersByBankName && cache.bankOffersByBankName[decodedBankName]) {
          offersArray = cache.bankOffersByBankName[decodedBankName];
        } else {
          const offerSnap = await getDoc(doc(db, "bank_offers", decodedBankName));
          if (!offerSnap.exists()) {
            setError("Bank offers document not found for this bank.");
            setLoading(false);
            return;
          }
          const data = offerSnap.data();
          offersArray = Array.isArray(data.offers) ? data.offers : [];

          cache.bankOffersByBankName = cache.bankOffersByBankName || {};
          cache.bankOffersByBankName[decodedBankName] = offersArray;
        }

        const matchedOffer = offersArray.find((o) => o.id === offerId);

        if (!matchedOffer) {
          setError("Offer ID not found in bank offers.");
          setLoading(false);
          return;
        }

        setOffer(matchedOffer);

        if (!cache.banksLogos || !cache.banksAddresses) {
          const banksSnap = await getDocs(collection(db, "banks"));
          const logosMap = {};
          const addressesMap = {};
          banksSnap.forEach((doc) => {
            const d = doc.data();
            logosMap[doc.id] = d.logo || "";
            addressesMap[doc.id] = d.address || "";
          });
          cache.banksLogos = logosMap;
          cache.banksAddresses = addressesMap;
        }

        setBanksLogos(cache.banksLogos);
        setBanksAddresses(cache.banksAddresses);
      } catch (err) {
        setError("Failed to load data: " + err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [decodedBankName, offerId]);

  if (loading) return <p>Loading bank offer details...</p>;
  if (error)
    return (
      <div>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
          type="button"
        >
          Go Back
        </button>
      </div>
    );

  if (!offer) return null;

  const bankLogo = banksLogos[decodedBankName];
  const bankAddress = banksAddresses[decodedBankName];
  const pageUrl = window.location.href;

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded shadow relative">
      <style>{`
        @keyframes glow {
          0%, 100% {
            box-shadow: 0 0 6px 3px rgba(59, 130, 246, 0.6);
          }
          50% {
            box-shadow: 0 0 14px 6px rgba(59, 130, 246, 1);
          }
        }
      `}</style>

      <div className="absolute top-4 left-4">
        <button
          onClick={() => navigate(-1)}
          className="px-3 py-1 bg-gray-300 rounded hover:bg-gray-400"
          type="button"
          aria-label="Back"
        >
          ← Back
        </button>
      </div>

      {/* Offer icon with overlapping glowing bank logo */}
      <div className="relative w-48 h-48 mx-auto mb-6">
        {offer.icon && (
          <img
            src={offer.icon}
            alt={`${offer.vendor} offer`}
            className="w-48 h-48 object-contain rounded-md shadow"
          />
        )}
        {bankLogo && (
          <div
            className="absolute top-0 right-0 w-16 h-16 rounded-full border-2 border-white bg-white p-1"
            style={{
              transform: "translate(25%, -25%)",
              animation: "glow 2s infinite",
            }}
          >
            <img
              src={bankLogo}
              alt={`${decodedBankName} logo`}
              className="w-full h-full object-contain rounded-full"
            />
          </div>
        )}
      </div>

      <h1 className="text-3xl font-bold mb-1 text-center">{offer.vendor && offer.vendor.includes("##") ? offer.category : offer.vendor}</h1>

      {decodedBankName && (
        <p className="text-center text-sm text-gray-500 mb-4">{decodedBankName}</p>
      )}

      <p className="text-center text-xl text-indigo-600 font-semibold mb-4">{offer.offer}</p>

      {offer.expiryDttm && (
        <p className="text-center text-gray-500 mb-2">
          Expires:{" "}
          {offer.expiryDttm.toDate
            ? offer.expiryDttm.toDate().toLocaleDateString()
            : new Date(offer.expiryDttm).toLocaleDateString()}
        </p>
      )}

      {offer.minimumSpend && (
        <p className="text-center text-gray-600 mb-2">
          Minimum Spend: ${offer.minimumSpend}
        </p>
      )}

      {offer.maximumOffer && (
        <p className="text-center text-gray-600 mb-4">
          Max Discount: ${offer.maximumOffer}
        </p>
      )}

      {bankAddress && (
        <div className="text-center mb-4">
          <a
            href={bankAddress}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
          >
            Visit Bank Site
          </a>
        </div>
      )}

      <ShareButtons url={pageUrl} title={offer.vendor} offer={offer} />
    </div>
  );
}
